/**
 * Which side of the quote paints the live candle.
 *
 * The chart's last bar is written from two sources that must agree:
 *   - server 1-minute OHLC batches (`GetOHLCData` → the ViiSync engine), and
 *   - the local live-tick pump in `Chart.tsx` (`flushLiveCandleImpl`).
 *
 * The engine builds its bars from the tick's LAST price — verified against prod
 * XAUUSD on 2026-08-17, where five consecutive 1m bars matched the per-minute
 * open/high/low/close of the tick `Last` series exactly, and matched neither the
 * bid nor the ask series. The pump used to paint `quote.bid`, so every server
 * batch snapped the last candle by the bid/last offset while the ASK/BID overlay
 * lines — drawn from the same quote — kept moving. That reads on screen as the
 * candles drifting independently of the bid/ask lines.
 *
 * `last` is not guaranteed to be populated on every feed, so fall back to the
 * mid and finally to the bid rather than dropping the tick.
 */
export interface LivePriceSides {
  bid?: number | null;
  ask?: number | null;
  last?: number | null;
}

export function pickLiveCandlePrice(quote: LivePriceSides): number | null {
  const last = quote.last;
  if (typeof last === "number" && Number.isFinite(last) && last > 0) {
    return last;
  }
  const bid = quote.bid;
  const ask = quote.ask;
  const bidOk = typeof bid === "number" && Number.isFinite(bid) && bid > 0;
  const askOk = typeof ask === "number" && Number.isFinite(ask) && ask > 0;
  if (bidOk && askOk) return (bid! + ask!) / 2;
  if (bidOk) return bid!;
  if (askOk) return ask!;
  return null;
}
